import { useEffect, useMemo, useRef, useState } from 'react';
import type { AgoraParticipantLike, AgoraPublicationLike } from '../../types';
import useVideoContext from '../useVideoContext/useVideoContext';
import { sameAgoraParticipant } from '../../utils/sameAgoraParticipant';

function buildLocalPublications(localTracks: any[]): AgoraPublicationLike[] {
  const out: AgoraPublicationLike[] = [];
  localTracks.forEach(track => {
    const kind = (track as any).trackMediaType ?? (track as any).kind;
    const trackName = (track as any).trackName ?? (track as any).name ?? '';
    if (kind === 'audio') {
      out.push({ kind: 'audio', track, trackName: 'audio' });
      return;
    }
    if (kind === 'video') {
      out.push({
        kind: 'video',
        track,
        trackName: trackName.includes('screen') ? 'screen' : 'camera',
      });
    }
  });
  return out;
}

export default function usePublications(participant: AgoraParticipantLike | null): AgoraPublicationLike[] {
  const [publications, setPublications] = useState<AgoraPublicationLike[]>([]);
  const [localTrackStateVersion, setLocalTrackStateVersion] = useState(0);
  const { room, localTracks } = useVideoContext();
  const participantRef = useRef(participant);
  participantRef.current = participant;
  const isLocalParticipant = sameAgoraParticipant(participant, room?.localParticipant);

  const stableKey = participant ? `${participant.identity}-${participant.uid}` : '';
  const localPublications = useMemo(
    () => buildLocalPublications(localTracks),
    [localTracks, localTrackStateVersion]
  );

  useEffect(() => {
    if (!isLocalParticipant) return;
    const bump = () => setLocalTrackStateVersion(v => v + 1);
    const cleanups: Array<() => void> = [];

    localTracks.forEach(track => {
      if (!track) return;
      const on = (track as any).on;
      const off = (track as any).off;
      if (typeof on === 'function' && typeof off === 'function') {
        on.call(track, 'track-updated', bump);
        on.call(track, 'enabled', bump);
        on.call(track, 'disabled', bump);
        cleanups.push(() => {
          off.call(track, 'track-updated', bump);
          off.call(track, 'enabled', bump);
          off.call(track, 'disabled', bump);
        });
      }
      const mst: MediaStreamTrack | undefined =
        typeof (track as any).getMediaStreamTrack === 'function'
          ? (track as any).getMediaStreamTrack()
          : (track as any).mediaStreamTrack;
      if (mst) {
        mst.addEventListener('mute', bump);
        mst.addEventListener('unmute', bump);
        mst.addEventListener('ended', bump);
        cleanups.push(() => {
          mst.removeEventListener('mute', bump);
          mst.removeEventListener('unmute', bump);
          mst.removeEventListener('ended', bump);
        });
      }
    });

    return () => cleanups.forEach(fn => fn());
  }, [isLocalParticipant, localTracks]);

  useEffect(() => {
    // Local participant publications should come from localTracks directly (reactive source),
    // not from participant snapshots that can lag in speaker view.
    if (isLocalParticipant) return;

    if (!participant) {
      setPublications([]);
      return;
    }

    const refresh = () => {
      const p = participantRef.current;
      if (!p) {
        setPublications([]);
        return;
      }
      setPublications(p.getPublications());
    };

    refresh();

    const client = room?.client;
    if (!client) return;

    client.on('user-published', refresh);
    client.on('user-unpublished', refresh);
    client.on('user-joined', refresh);
    client.on('user-left', refresh);

    return () => {
      client.off('user-published', refresh);
      client.off('user-unpublished', refresh);
      client.off('user-joined', refresh);
      client.off('user-left', refresh);
    };
    // `room` (not only `client`) must be in deps: after async subscribe, participants update but client ref is unchanged.
  }, [stableKey, room, participant, isLocalParticipant]);

  return isLocalParticipant ? localPublications : publications;
}

